import { differenceInHours, differenceInMinutes } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// 扩展 dayjs 支持时区功能
dayjs.extend(utc);
dayjs.extend(timezone);

// 计算开始时间与结束时间的时间差
export const durationBetween = (start: Date, end: Date) => {
    const hours = differenceInHours(end, start);
    const minutes = differenceInMinutes(end, start) % 60;
    return `${hours}hr ${minutes}min`;
}

// 计算当前时间与开始时间的时间差
export const durationBetweenNow = (start: Date | string) => {
    const now = new Date();
    return durationBetween(new Date(start), now);
}

// 计算当前时间与开始时间的时间差
export const timeLeftNowMinutes = (start: Date | string) => {
    const now = new Date();
    return differenceInMinutes(new Date(start), now);
}

/**
 * 将ISO 8601格式的日期字符串转换为YYYY/MM/DD格式
 */
export const formatDate = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}/${month}/${day}`;
    } catch (error) {
        return dateString;
    }
};
/**
 * 将ISO 8601格式的事件字符串转换为YYYY/MM/DD HH:mm:SS格式
 */
export const formatTimeString = (timeString: string): string => {
    try {
        const date = new Date(timeString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
        return timeString;
    }
}

/**
 * 将ISO 8601格式的日期字符串转换为YYYY/MM/DD HH:mm:ss格式
 * 保持原始时区，不进行时区转换
 */
export const formatISODateTime = (dateString: string): string => {
    try {
        // 使用 dayjs 解析
        const date = dayjs(dateString);
        if (!date.isValid()) {
            return dateString;
        }
        return date.format('YYYY/MM/DD HH:mm:ss');
    } catch (error) {
        return dateString;
    }
};

export const timestampToDate = (timestamp: number | string): Date => {
    if (typeof timestamp === 'string') {
        timestamp = Number(timestamp);
    }
    // 如果是10位数字，认为是秒时间戳
    if (timestamp.toString().length === 10) {
        return new Date(timestamp * 1000);
    }
    // 如果是13位数字，认为是毫秒时间戳
    return new Date(timestamp);
};

/**
 * 将时间戳格式化为YYYY-MM-DD HH:mm:ss格式
 * @param timestamp 时间戳
 * @returns 格式化后的时间字符串
 */
export const formatTimestampToDateTime = (timestamp: number | string): string => {
    return dayjs(timestampToDate(Number(timestamp))).format('YYYY/MM/DD HH:mm:ss');
}

/**
 * 可空时间戳格式化：空值（null/undefined/0/空串）返回 '-'，否则委托 formatTimestampToDateTime。
 * 用于接口返回 epoch 秒/毫秒时间戳的展示位（例如客诉/申诉详情）。
 */
export const formatEpochToDateTime = (timestamp?: number | string | null): string => {
    if (timestamp == null || timestamp === '' || Number(timestamp) === 0) return '-';
    return formatTimestampToDateTime(timestamp);
}

/**
 * 将时间范围格式化为ISO标准格式
 */
export const formatTimeRangeToISO = (timeRange: [string | null, string | null] | null | undefined) => {
    if (!timeRange) return undefined;

    const startDate = timeRange[0] ? dayjs(timeRange[0]) : null;
    const endDate = timeRange[1] ? dayjs(timeRange[1]) : null;

    return {
        start: startDate?.format('YYYY-MM-DDT00:00:00.000[Z]'),
        end: endDate?.format('YYYY-MM-DDT23:59:59.999[Z]'),
    };
};

/**
 * 把"只选日期"的范围 + 可选 IANA 时区，转换成通用 { start, end } 墙钟时间结构（与后端 TimeRangeType 字段对齐）。
 * 开始日 -> 该时区当日 00:00:00，结束日 -> 该时区当日 23:59:59（精确到秒，无毫秒）；
 * 不选时区则按 UTC 换算。timeZoneId 不内嵌在返回结构里——它作为独立参数单独传给后端，
 * 页面上如果有多个时间筛选，可以共用同一个 timeZoneId，不必每个时间范围各自带一份。
 * 与 BillTimeRangePicker 内部的 buildDateOnlyBillTimeRange 是同一逻辑，因不依赖具体业务类型而抽成通用函数。
 */
export const buildDateOnlyTimeRange = (
    dateRange: [string | null, string | null] | null | undefined,
    timeZoneId?: string | null
): { start: string; end: string } | undefined => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return undefined;

    const zone = timeZoneId || 'UTC';
    const startDateStr = dayjs(dateRange[0]).format('YYYY-MM-DD');
    const endDateStr = dayjs(dateRange[1]).format('YYYY-MM-DD');

    return {
        start: dayjs.tz(startDateStr, zone).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        end: dayjs.tz(endDateStr, zone).endOf('day').format('YYYY-MM-DD HH:mm:ss')
    };
};

/**
 * 按本地日期的 0:00 与 23:59:59.999 转为 UTC 的 ISO 范围，用于日期筛选（选几号即查该日整天）
 */
export const formatTaskDateRangeToISO = (timeRange: [string | null, string | null] | null | undefined) => {
    if (!timeRange || timeRange[0] == null || timeRange[1] == null) return null;
    const start = dayjs(timeRange[0]).startOf('day').toISOString();
    const end = dayjs(timeRange[1]).endOf('day').toISOString();
    return { start, end };
};

/**
 * 将时间范围格式化为ISO标准格式
 * 如果传入的已经是完整的ISO字符串，直接返回；否则按日期处理
 */
export const formatDateTimeRangeToISO = (timeRange: [string | null, string | null] | null | undefined) => {
    if (!timeRange) return undefined;

    const startDate = timeRange[0] ? dayjs(timeRange[0]) : null;
    const endDate = timeRange[1] ? dayjs(timeRange[1]) : null;

    return {
        start: startDate?.toISOString(),
        end: endDate?.toISOString()
    };
};

/**
 * 将 { start, end } 形式的时间范围转为 ISO，仅当 start、end 均有效时返回，否则返回 undefined。
 * 用于满足 GraphQL TimeRangeTypeInput 等需要必填 start/end 的场景。
 */
export const formatTimeRangeObjectToISO = (
    range: { start?: string | null; end?: string | null } | null | undefined
): { start: string; end: string } | undefined => {
    const r = formatDateTimeRangeToISO(
        range ? [range.start ?? null, range.end ?? null] : undefined
    );
    return r && r.start != null && r.end != null ? { start: r.start, end: r.end } : undefined;
};


export const timestampToDayjs = (timestamp: number | string): dayjs.Dayjs => {
    return dayjs(timestampToDate(Number(timestamp)));
}

/**
 * 创建日期范围限制函数，用于 RangePicker 的 disabledDate
 * @param maxDays 最大天数范围
 * @param dateRange 当前选择的日期范围 [startDate, endDate]
 * @param tempDateRange 临时日期范围（用户正在选择时的范围）
 * @returns disabledDate 函数
 */
export const createDateRangeLimit = (
    maxDays: number,
    dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null],
    tempDateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
) => {
    return (current: dayjs.Dayjs | null): boolean => {
        if (!current) return false;

        const now = dayjs();
        // 禁用未来日期
        if (current.isAfter(now, 'day')) {
            return true;
        }

        // 使用临时日期范围（如果存在）或已保存的日期范围
        const range = tempDateRange || dateRange;
        const [startDate, endDate] = range;

        // 如果已选择开始日期，限制结束日期不能超过开始日期+maxDays天
        if (startDate && !endDate) {
            const maxEndDate = startDate.add(maxDays, 'day');
            return current.isAfter(maxEndDate, 'day') || current.isBefore(startDate, 'day');
        }

        // 如果已选择结束日期，限制开始日期不能早于结束日期-maxDays天
        if (endDate && !startDate) {
            const minStartDate = endDate.subtract(maxDays, 'day');
            return current.isBefore(minStartDate, 'day') || current.isAfter(endDate, 'day');
        }

        // 如果两个日期都已选择，需要根据用户正在选择哪个日期来限制
        if (startDate && endDate) {
            // 如果临时日期范围存在，说明用户正在重新选择
            if (tempDateRange) {
                const [tempStart, tempEnd] = tempDateRange;
                // 如果只有临时开始日期，说明用户正在选择结束日期
                if (tempStart && !tempEnd) {
                    const maxEndDate = tempStart.add(maxDays, 'day');
                    return current.isAfter(maxEndDate, 'day') || current.isBefore(tempStart, 'day');
                }
                // 如果只有临时结束日期，说明用户正在选择开始日期
                if (tempEnd && !tempStart) {
                    const minStartDate = tempEnd.subtract(maxDays, 'day');
                    return current.isBefore(minStartDate, 'day') || current.isAfter(tempEnd, 'day');
                }
                // 如果两个临时日期都存在，使用临时日期范围
                if (tempStart && tempEnd) {
                    // 判断当前日期是在选择开始还是结束
                    if (current.isBefore(tempStart, 'day')) {
                        const minStartDate = tempEnd.subtract(maxDays, 'day');
                        return current.isBefore(minStartDate, 'day');
                    }
                    if (current.isAfter(tempEnd, 'day')) {
                        const maxEndDate = tempStart.add(maxDays, 'day');
                        return current.isAfter(maxEndDate, 'day');
                    }
                }
            }
            // 如果没有临时日期范围，使用已保存的日期范围来限制
            // 如果当前日期在开始日期之前，说明用户想修改开始日期，限制在结束日期-maxDays天到结束日期之间
            if (current.isBefore(startDate, 'day')) {
                const minStartDate = endDate.subtract(maxDays, 'day');
                return current.isBefore(minStartDate, 'day');
            }
            // 如果当前日期在结束日期之后，说明用户想修改结束日期，限制在开始日期到开始日期+maxDays天之间
            if (current.isAfter(endDate, 'day')) {
                const maxEndDate = startDate.add(maxDays, 'day');
                return current.isAfter(maxEndDate, 'day');
            }
            // 如果当前日期在范围内，允许选择（用户可能想重新选择）
            return false;
        }

        return false;
    };
};

/**
 * 将时间范围从指定时区转换为UTC时区的ISO格式
 * @param timeRange 时间范围 [startDate, endDate]
 * @param timezone 时区字符串，格式如 "+8", "-7", "+0"
 * @returns UTC时间范围对象 {start: string, end: string}
 */
export const formatTimeRangeWithTimezone = (
    timeRange: [string | null, string | null] | null | undefined,
    timezone?: string | null
) => {
    if (!timeRange) return undefined;

    const startDate = timeRange[0] ? dayjs(timeRange[0]) : null;
    const endDate = timeRange[1] ? dayjs(timeRange[1]) : null;

    // 如果没有指定时区，使用默认的UTC时区
    if (!timezone) {
        return {
            start: startDate?.format('YYYY-MM-DDT00:00:00.000[Z]'),
            end: endDate?.format('YYYY-MM-DDT23:59:59.999[Z]'),
        };
    }

    // 解析时区偏移量（例如 "+8" -> 8, "-7" -> -7）
    const timezoneOffset = parseInt(timezone, 10);

    // 将本地时间转换为UTC时间
    // 例如：如果选择的是UTC+8时区，输入的是当地时间 2024-01-01 00:00:00
    // 需要减去8小时得到UTC时间 2023-12-31 16:00:00
    const startUTC = startDate?.subtract(timezoneOffset, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
    const endUTC = endDate?.subtract(timezoneOffset, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

    return {
        start: startUTC,
        end: endUTC,
    };
};

/**
 * 将本地时间字符串转换为 UTC 时间
 * 支持 IANA 时区标识符（如 America/New_York）、UTC 偏移格式（如 UTC-5, UTC+8）和 GMT 格式（如 GMT-5, GMT+8）
 * @param dateString 本地时间字符串，格式为 YYYY-MM-DD HH:mm:ss
 * @param timezone 时区字符串，可以是：
 *   - IANA 标识符：America/New_York, Asia/Shanghai 等
 *   - UTC 偏移：UTC-5, UTC+8, UTC-05:00, UTC+02:00
 *   - GMT 偏移：GMT-5, GMT+8, GMT-05:00, GMT+02:00
 *   - 纯偏移：+5, -7, +02:00, -05:00
 * @returns dayjs 对象，已转换为 UTC 时区
 */
export const convertLocalTimeToUtc = (dateString: string, timezone: string): dayjs.Dayjs => {
    try {
        // 首先尝试作为 IANA 时区标识符处理
        try {
            const targetZoneTime = dayjs.tz(dateString, timezone);
            // 检查转换是否成功（dayjs 在失败时会使用默认时区）
            if (targetZoneTime.isValid()) {
                return targetZoneTime.utc();
            }
        } catch (e) {
            // IANA 标识符处理失败，继续尝试偏移格式
        }

        // 尝试处理 UTC 或 GMT 偏移格式（如 UTC-5, UTC+8, GMT-5, GMT+8, +5, -7）
        const utcOrGmtOffsetMatch = timezone.match(/^(UTC|GMT)?([+-])(\d{1,2})$/i);
        if (utcOrGmtOffsetMatch) {
            const sign = utcOrGmtOffsetMatch[2] === '+' ? 1 : -1;
            const offsetHours = sign * parseInt(utcOrGmtOffsetMatch[3], 10);

            // 将本地时间字符串解析为 UTC 对象（使用 utc() 确保不受浏览器时区影响）
            const localTime = dayjs.utc(dateString);

            // 手动计算 UTC 时间：
            // 本地时间 = UTC 时间 + 偏移量
            // 所以：UTC 时间 = 本地时间 - 偏移量
            // 例如：本地时间 10:00 在 UTC-5 区
            // UTC 时间 = 10:00 - (-5) = 10:00 + 5 = 15:00
            return localTime.subtract(offsetHours, 'hour');
        }

        // 如果都失败了，直接返回 UTC 时间
        console.warn(`Failed to parse timezone '${timezone}', using local time as UTC`);
        return dayjs(dateString).utc();
    } catch (error) {
        console.error(`Error converting time to UTC with timezone '${timezone}':`, error);
        return dayjs(dateString).utc();
    }
};

/**
 * 获取最近一周的日期范围（用于日期筛选器默认值）
 * @param format 日期格式，默认为 'YYYY/MM/DD'
 * @returns 日期范围数组 [startDate, endDate]
 */
export const getLastWeekDateRange = (format: string = 'YYYY/MM/DD'): [string, string] => {
    const end = dayjs();
    const start = dayjs().subtract(7, 'day');
    return [
        start.format(format),
        end.format(format)
    ];
};

/**
 * 获取最近一周的日期时间范围（用于日期时间筛选器默认值，使用本机“当前时间”作为结束时间）
 * @returns ISO 字符串格式的日期时间范围数组 [startISO, endISO]
 */
export const getLastWeekDateTimeRange = (): [string, string] => {
    // 使用本机当前时间（而非 0 点），确保用户刷新同一页面时默认时间可“滚动”
    const end = dayjs();
    const start = dayjs().subtract(7, 'day');
    return [
        start.format('YYYY-MM-DD HH:mm:ss'),
        end.format('YYYY-MM-DD HH:mm:ss')
    ];
};

